import { IApiBaseProps, IResultResponse } from '@common/api.models';
import { createGetOptionsWithSearchFn } from '@common/selectOptions.services';
import { envConfigs } from '@config/envConfigs';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import { apiServerDelete, apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import {
  IProjectUserProfileOption,
  IProjectUserProfile,
  ICreateProjectUserProfileRequest,
  IUpdateProjectUserProfileRequest,
} from './projectUserProfile.models';

export const apiRoutes = {
  getAllProjectUserProfiles: '/UserProfiles/ProjectUserProfiles',
  getProjectUserProfile: '/UserProfiles/ProjectUserProfiles',
  createProjectUserProfile: '/UserProfiles/ProjectUserProfiles',
  updateProjectUserProfile: '/UserProfiles/ProjectUserProfiles',
  getProjectUserProfileOptionsByCompany: '/UserProfiles/ProjectUserProfiles/Options/ByCompany',
  removeUserFromCompany: (id: number, companyId: number) =>
    `/UserProfiles/ProjectUserProfiles/${id}/Company/${companyId}`,
  lockProjectUserProfile: '/UserProfiles/ProjectUserProfiles/Lock',
  unlockProjectUserProfile: '/UserProfiles/ProjectUserProfiles/Unlock',
  searchProjectUserProfileOptions: '/UserProfiles/ProjectUserProfiles/Options/Search',
};

export const getAllProjectUserProfilesAsync = async (
  props: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectUserProfile>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getAllProjectUserProfiles}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export interface IGetProjectUserProfileProps extends IApiBaseProps {
  id: number;
}

export const getProjectUserProfileAsync = async ({
  id,
  ...props
}: IGetProjectUserProfileProps): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerGet<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getProjectUserProfile}/${id}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export interface IGetProjectUserProfileOptionsByCompanyProps extends IApiBaseProps {
  companyId: number;
}

export const getProjectUserProfileOptionsByCompanyAsync = async ({
  ...props
}: IGetProjectUserProfileOptionsByCompanyProps): Promise<IPagedResultsV1<IProjectUserProfileOption>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectUserProfileOption>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getProjectUserProfileOptionsByCompany}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const createProjectUserProfileAsync = async (
  createRequest: ICreateProjectUserProfileRequest,
): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerPost<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.createProjectUserProfile}`,
    createRequest,
  );
  return result.data;
};

export const updateProjectUserProfileAsync = async (
  updateRequest: IUpdateProjectUserProfileRequest,
): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateProjectUserProfile}/${updateRequest.userProfile.id}`,
    updateRequest,
  );
  return result.data;
};

export interface IRemoveUserFromCompanyProps extends IApiBaseProps {
  companyId: number;
  projectUserProfileId: number;
}

export const removeUserFromCompanyAsync = async ({
  projectUserProfileId,
  companyId,
  ...props
}: IRemoveUserFromCompanyProps): Promise<IResultResponse> => {
  const result = await apiServerDelete<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.removeUserFromCompany(projectUserProfileId, companyId)}`,
    { params: { ...props } },
  );
  return result.data;
};

export const toProjectUserProfileOption = (projectUserProfile: IProjectUserProfile): IProjectUserProfileOption => {
  const { id, userProfileId, userProfile, projectId } = projectUserProfile;
  return {
    id: id || 0,
    userProfileId,
    projectId: projectId || 0,
    uid: userProfile?.uid || '',
    email: userProfile?.email || '',
    upn: userProfile?.upn || '',
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    ekpid: userProfile?.ekpid || '',
    employeeId: userProfile?.employeeId || '',
    displayName: userProfile?.displayName,
    rowVersion: userProfile?.rowVersion || '',
  };
};

export interface ILockUnlockProjectUserProfileProps extends IApiBaseProps {
  id: number;
}

export const lockProjectUserProfileAsync = async ({
  id,
  ...props
}: ILockUnlockProjectUserProfileProps): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.lockProjectUserProfile}/${id}`,
    {},
    { params: { ...props } },
  );
  return result.data;
};

export const unlockProjectUserProfileAsync = async ({
  id,
  ...props
}: ILockUnlockProjectUserProfileProps): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.unlockProjectUserProfile}/${id}`,
    {},
    { params: { ...props } },
  );
  return result.data;
};

export const getProjectUserProfileOptionsWithSearchAsync = createGetOptionsWithSearchFn<IProjectUserProfileOption>(
  apiRoutes.searchProjectUserProfileOptions,
);
